import { BlockingPopupModal } from "@/components";
import PageWrapper from "@/components/PageWrapper/PageWrapper";
import { PromoteToRoadmap } from "@/components/StickyActionsHeader/SaveActions/PromoteToRoadmap";
import { SaveAndCancel } from "@/components/StickyActionsHeader/SaveActions/SaveAndCancel";
import { StickyActionsHeader } from "@/components/StickyActionsHeader/StickyActionsHeader";
import { FieldNames, FormType } from "@/enums";
import { IFile } from "@/interfaces/IFile";
import { ISelectOption } from "@/interfaces";
import { useFormPropertyValueLookupProvider } from "@/providers";
import {
    useCreateInitiativeMutation,
    useDeleteUploadsMutation,
    useGetCDNFilePathQuery,
    useGetFormLookupsQuery,
    useGetInitiativeDocumentsQuery,
    useGetInitiativeProjectTypesQuery,
    useGetInitiativeQuery,
    usePromoteInitiativeMutation,
    useSoftDeleteInitiativeDocumentMutation,
    useUpdateInitiativeMutation,
    useUploadInitiativeDocumentMutation,
    useGetInitiativeEngineeringComplexitiesQuery,
    useGetInitiativeCommercialComplexitiesQuery,
    useGetInitiativeFitToServiceOptionsQuery, useGetInitiativeOpportunityOptionsQuery,
    useGetAdminFlatFormsQuery
} from "@/services";
import { Form, UploadFile, UploadProps } from "antd";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { v4 } from "uuid";
import dayjs from "dayjs";
import { InitiativeFormFields } from "./components/InitiativeFormFields";
import { InitiativePreviewModal } from "./components/InitiativePreviewModal";
import { routes } from "@/helpers/routes";
import { getPromoteValidationMessages } from "./helper/getPromoteValidationMessages";
import { getProductLineOptions } from "./helper/getProductLineOptions";

const PAGE_NAME = "initiative";

interface IInitiativeFormValues {
  name: string;
  projectType: string;
  productCategory: string;
  estimatedTargetLaunchDate: dayjs.Dayjs;
  documents: IFile[];
  productLine0: string;
  productLine1: string;
  productLine2: string;
  sbuPitchedDate: dayjs.Dayjs | null;
  opportunityType: string | null;
  digital: boolean;
  yearOneNetSalesNorthAmerica: number | null;
  grossMarginPercentage: number | null;
  engineeringComplexity: string | null;
  commercialComplexity: string | null;
  fitToService: string | null;
}

const PROMOTION_FIELD_NAMES: (keyof IInitiativeFormValues)[] = [
  "productLine0",
  "productLine1",
  "productLine2",
  "sbuPitchedDate",
  "opportunityType",
  "yearOneNetSalesNorthAmerica",
  "grossMarginPercentage",
  "engineeringComplexity",
  "commercialComplexity",
  "fitToService",
];

const isPromotionFieldEmpty = (value: unknown): boolean => {
  if (value == null || value === "") {
    return true;
  }

  return typeof value === "number" && Number.isNaN(value);
};

const toInitiativePayload = (values: IInitiativeFormValues) => ({
  name: values.name,
  publicProductCategoryId: values.productCategory,
  publicProjectTypeId: values.projectType,
  estimatedTargetLaunchDate: values.estimatedTargetLaunchDate.format("YYYY-MM-DD"),
  productLine0: values.productLine0,
  productLine1: values.productLine1,
  productLine2: values.productLine2,
  sbuPitchedDate: values.sbuPitchedDate?.format("YYYY-MM-DD") ?? null,
  initiativeOpportunityTypeId:
    values.opportunityType != null ? Number(values.opportunityType) : null,
  initiativeEngineeringComplexityId:
    values.engineeringComplexity != null
      ? Number(values.engineeringComplexity)
      : null,
  initiativeCommercialComplexityId:
    values.commercialComplexity != null
      ? Number(values.commercialComplexity)
      : null,
  initiativeFitToServiceId:
    values.fitToService != null ? Number(values.fitToService) : null,
  digital: values.digital ?? false,
  yearOneNetSalesNorthAmerica: values.yearOneNetSalesNorthAmerica,
  grossMarginPercentage: values.grossMarginPercentage,
});

export const Initiative = (): React.JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { initiativePublicId } = useParams();
  const [form] = Form.useForm<IInitiativeFormValues>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [projectTypeOptions, setProjectTypeOptions] = useState<
    ISelectOption[]
  >([]);
  const [opportunityTypeOptions, setOpportunityTypeOptions] = useState<ISelectOption[]>([]);
  const [engineeringComplexityOptions, setEngineeringComplexityOptions] = useState<ISelectOption[]>([]);
  const [commercialComplexityOptions, setCommercialComplexityOptions] = useState<ISelectOption[]>([]);
  const [fitToServiceOptions, setFitToServiceOptions] = useState<ISelectOption[]>([]);
  
  const [createInitiative] = useCreateInitiativeMutation();
  const [updateInitiative] = useUpdateInitiativeMutation();
  const [uploadInitiativeDocument] = useUploadInitiativeDocumentMutation();
  const [softDeleteInitiativeDocument] = useSoftDeleteInitiativeDocumentMutation();
  const [promoteInitiative] = usePromoteInitiativeMutation();
  const [isPromoting, setIsPromoting] = useState<boolean>(false);
  const [hasPromoted, setHasPromoted] = useState<boolean>(false);
  const [deleteUploads] = useDeleteUploadsMutation();
  const [placeholderId] = useState<string>(() => v4());
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<UploadFile[]>([]);
  const [fileId, setFileId] = useState<string | null>(null);
  const { data: cdnFilePath } = useGetCDNFilePathQuery(
    {
      projectId: initiativePublicId,
      fileId,
      isInitiative: true,
    },
    { skip: !fileId }
  );

  const { data: { flatForms: adminFormFields = [] } = {} } =
    useGetAdminFlatFormsQuery(null);
  const { data: formLookups } = useGetFormLookupsQuery();
  const { data: initiativeProjectTypes } = useGetInitiativeProjectTypesQuery();
  const { data: initiativeEngineeringComplexities } = useGetInitiativeEngineeringComplexitiesQuery();
  const { data: initiativeComplexityComplexities } = useGetInitiativeCommercialComplexitiesQuery();
  const { data: initiativeFitToServices } = useGetInitiativeFitToServiceOptionsQuery();
  const { data: initiativeOpportunityTypes } = useGetInitiativeOpportunityOptionsQuery();
  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();
  const { data: initiative } = useGetInitiativeQuery(initiativePublicId, {
    skip: !initiativePublicId,
  });
  const { data: initiativeDocuments } = useGetInitiativeDocumentsQuery(
    initiativePublicId,
    {
      skip: !initiativePublicId,
    }
  );
  const isEditing = Boolean(initiativePublicId);
  const isUploadingDocuments = fileList.some(
    (file) => file.status === "uploading"
  );
  const existingDocumentIdsRef = useRef<Set<string>>(new Set());
  const pendingDocumentRemovalsRef = useRef<Map<string, string>>(new Map());
  const navigateAfterSaveRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hasUnsavedChanges && navigateAfterSaveRef.current) {
      const path = navigateAfterSaveRef.current;
      navigateAfterSaveRef.current = null;
      navigate(path);
    }
  }, [hasUnsavedChanges, navigate]);

  // TODO: validation of "Required for Roadmap" fields is handled by the
  // in-flight endpoint for now. Confirm the status field name once exposed.
  const canPromote = isEditing && !hasPromoted && initiative?.status === "Opportunity";

  const productLine0Options = Object.keys(formLookups?.intake?.productLine0 ?? {}).map((key) => ({
    value: key,
    label: getPropertyValueDisplay(
      FormType.Intake,
      FieldNames.ProductLine0,
      key
    ),
  }));
  let productLine1Options: ISelectOption[] = [];
  let productLine2Options: ISelectOption[] = [];

  const selectedPL0 = Form.useWatch("productLine0", form);
  if (selectedPL0 && formLookups) {
    productLine1Options = getProductLineOptions(formLookups, adminFormFields, getPropertyValueDisplay, FieldNames.ProductLine1, selectedPL0);
    productLine2Options = getProductLineOptions(formLookups, adminFormFields, getPropertyValueDisplay, FieldNames.ProductLine2, selectedPL0);
  }

  const productCategoryOptions = Object.keys(formLookups?.intake?.productCategory ?? {}).map((key) => ({
    value: key,
    label: getPropertyValueDisplay(
      FormType.Intake,
      FieldNames.ProductCategory,
      key
    ),
  }));

  useEffect(() => {
    if (!Array.isArray(initiativeProjectTypes)) return;

    const options: ISelectOption[] = initiativeProjectTypes.map(
      ({ publicId, name }) => {
        const translatedLabel = getPropertyValueDisplay(
          FormType.Intake,
          FieldNames.ProjectType,
          publicId
        );

        return {
          value: publicId,
          label: translatedLabel === publicId ? name : translatedLabel,
        };
      }
    );

    const savedProjectTypeId = initiative?.publicProjectTypeId;
    if (
      savedProjectTypeId &&
      !options.some((option) => option.value === savedProjectTypeId)
    ) {
      options.push({
        value: savedProjectTypeId,
        label: initiative.projectTypeName ?? savedProjectTypeId,
      });
    }

    setProjectTypeOptions(options);
  }, [initiativeProjectTypes, initiative, getPropertyValueDisplay]);

    useEffect(() => {
        if (!Array.isArray(initiativeEngineeringComplexities)) return;
        
        const options: ISelectOption[] = initiativeEngineeringComplexities.map(option => {
            return {
                value: String(option.id),
                label: option.name,
            };
        });
        
        setEngineeringComplexityOptions(options);
        
    }, [initiativeEngineeringComplexities]);

    useEffect(() => {
        if (!Array.isArray(initiativeComplexityComplexities)) return;

        const options: ISelectOption[] = initiativeComplexityComplexities.map(option => {
            return {
                value: String(option.id),
                label: option.name,
            };
        });

        setCommercialComplexityOptions(options);

    }, [initiativeComplexityComplexities]);

    useEffect(() => {
        if (!Array.isArray(initiativeFitToServices)) return;

        const options: ISelectOption[] = initiativeFitToServices.map(option => {
            return {
                value: String(option.id),
                label: option.name,
            };
        });

        setFitToServiceOptions(options);

    }, [initiativeFitToServices]);

    useEffect(() => {
        if (!Array.isArray(initiativeOpportunityTypes)) return;

        const options: ISelectOption[] = initiativeOpportunityTypes.map(option => {
            return {
                value: String(option.id),
                label: option.name,
            };
        });

        setOpportunityTypeOptions(options);

    }, [initiativeOpportunityTypes]);

  useEffect(() => {
    if (!initiative) return;

    form.setFieldsValue({
      name: initiative.name,
      projectType: initiative.publicProjectTypeId,
      productCategory: initiative.publicProductCategoryId,
      estimatedTargetLaunchDate: initiative.estimatedTargetLaunchDate
        ? dayjs(initiative.estimatedTargetLaunchDate)
        : undefined,
        productLine0: initiative.productLine0,
        productLine1: initiative.productLine1,
        productLine2: initiative.productLine2,
        sbuPitchedDate: initiative.sbuPitchedDate ?
          dayjs(initiative.sbuPitchedDate)
          : undefined,
        opportunityType: initiative.initiativeOpportunityTypeId != null ? String(initiative.initiativeOpportunityTypeId) : undefined,
        digital: initiative.digital ?? false,
        yearOneNetSalesNorthAmerica: initiative.yearOneNetSalesNorthAmerica,
        grossMarginPercentage: initiative.grossMarginPercentage,
        engineeringComplexity: initiative.initiativeEngineeringComplexityId != null ? String(initiative.initiativeEngineeringComplexityId) : undefined,
        commercialComplexity: initiative.initiativeCommercialComplexityId != null ? String(initiative.initiativeCommercialComplexityId) : undefined,
        fitToService: initiative.initiativeFitToServiceId != null ? String(initiative.initiativeFitToServiceId) : undefined,
    });
  }, [initiative, form]);

  useEffect(() => {
    if (!initiativeDocuments) return;

    const documentFileList: UploadFile[] = (
      initiativeDocuments as Array<{
        publicId: string;
        name: string;
        filePath: string;
        contentType: string;
      }>
    ).map((document) => ({
        uid: String(document.publicId),
        name: document.name,
        status: "done",
        url: document.filePath,
        blobUrl: document.filePath,
        imageUrl:
          document.contentType?.startsWith("image/") && document.filePath,
        type: document.contentType,
      })
    );

    setFileList(documentFileList);
    form.setFieldValue("documents", documentFileList);
    existingDocumentIdsRef.current = new Set(
      documentFileList.map((document) => document.uid)
    );
  }, [initiativeDocuments, form]);

  const setPreviewDoc = (document: UploadFile): void => {
    if (!existingDocumentIdsRef.current.has(document.uid)) {
      toast.error(intl.formatMessage({ id: "initiative.saveBeforePreview" }));
      return;
    }

    setFileId(document.uid);
    setPreviewFile([document]);
  };

  const downloadAndCloseModal = (filePath: string): void => {
    const link = document.createElement("a");
    link.href = filePath;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (fileId && previewFile && cdnFilePath) {
      setIsModalOpen(true);
    }
  }, [fileId, previewFile, cdnFilePath]);

  const handleRemoveDocument = ({ uid, url }: UploadFile): void => {
    if (existingDocumentIdsRef.current.has(uid)) {
      pendingDocumentRemovalsRef.current.set(uid, url ?? "");
      existingDocumentIdsRef.current.delete(uid);
    }

    setFileList((previousFileList) =>
      previousFileList.filter((file) => file.uid !== uid)
    );
    setHasUnsavedChanges(true);
  };

  const handleDocumentsChange: UploadProps["onChange"] = ({
    file: changedFile,
    fileList: updatedFileList,
  }) => {
    setFileList(updatedFileList);
    setHasUnsavedChanges(true);

    if (changedFile.status === "done") {
      form.setFieldValue("documents", updatedFileList);
    }
  };

  const handleCancel = (): void => {
    setHasUnsavedChanges(false);

    if (!initiativePublicId && fileList.length > 0) {
      void deleteUploads({ intakeId: placeholderId, isInitiative: false })
        .unwrap()
        .catch(() => {
          // Best-effort cleanup; don't block navigation if it fails.
        });
    }

    navigate("/initiatives");
  };

  const persistDocuments = async (savedInitiativePublicId: string): Promise<void> => {
    const newFiles = fileList.filter(
      (file) => !existingDocumentIdsRef.current.has(file.uid)
    );

    await Promise.all(
      newFiles.map(async (file) => {
        const uploadedFile = (file.response ?? file) as unknown as IFile;

        const publicInitiativeDocumentId = await uploadInitiativeDocument({
          initiativePublicId: savedInitiativePublicId,
          fileName: uploadedFile.name,
          filePath: uploadedFile.blobUrl,
          contentType: uploadedFile.type,
          blobListIds: uploadedFile.blobListIds,
        }).unwrap();

        existingDocumentIdsRef.current.add(publicInitiativeDocumentId);
        file.uid = publicInitiativeDocumentId;
      })
    );

    await Promise.all(
      Array.from(pendingDocumentRemovalsRef.current.entries()).map(
        async ([documentPublicId, filePath]) => {
          try {
            await softDeleteInitiativeDocument({
              initiativePublicId: savedInitiativePublicId,
              documentPublicId,
            }).unwrap();
          } catch (error) {
            console.error("Failed to soft delete initiative document", error);
            toast.error(intl.formatMessage({ id: "initiative.saveError" }));
            return;
          }

          const blobFolderPath = filePath.substring(
            0,
            filePath.lastIndexOf("/")
          );

          if (blobFolderPath) {
            try {
              await deleteUploads({
                intakeId: blobFolderPath,
                isInitiative: true,
              }).unwrap();
            } catch (error) {
              console.error("Failed to delete blob uploads", error);
              toast.error(intl.formatMessage({ id: "initiative.saveError" }));
            }
          }
        }
      )
    );
    pendingDocumentRemovalsRef.current.clear();
    setFileList((previousFileList) => [...previousFileList]);
  };

  const handlePromote = async (): Promise<void> => {
    if (!initiativePublicId) return;

    if (isUploadingDocuments) {
      toast.error(intl.formatMessage({ id: "initiative.documentsUploading" }));
      return;
    }

    let values: IInitiativeFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const missingPromotionFields = PROMOTION_FIELD_NAMES.filter((fieldName) =>
      isPromotionFieldEmpty(values[fieldName])
    );

    if (missingPromotionFields.length > 0) {
      form.setFields(
        missingPromotionFields.map((name) => ({
          name,
          errors: [intl.formatMessage({ id: "general.fieldRequiredMessage" })],
        }))
      );
      return;
    }

    setIsPromoting(true);
    try {
      await persistDocuments(initiativePublicId);
    } catch {
      toast.error(intl.formatMessage({ id: "initiative.saveError" }));
      setIsPromoting(false);
      return;
    }

    try {
      await promoteInitiative({
        initiativePublicId,
        ...toInitiativePayload(values),
      }).unwrap();
      setHasPromoted(true);
      setHasUnsavedChanges(false);
      toast.success(intl.formatMessage({ id: "initiative.promoteSuccess" }));
    } catch (error) {
      const validationMessages = getPromoteValidationMessages(error);

      if (validationMessages.length === 0) {
        toast.error(intl.formatMessage({ id: "initiative.promoteError" }));
      } else {
        toast.error(
          <div>
            {validationMessages.map((message) => (
              <div key={message}>{message}</div>
            ))}
          </div>
        );
      }
    } finally {
      setIsPromoting(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    if (isUploadingDocuments) {
      toast.error(intl.formatMessage({ id: "initiative.documentsUploading" }));
      return;
    }

    form.setFields(
      PROMOTION_FIELD_NAMES.map((name) => ({
        name,
        errors: [],
      }))
    );

    let values: IInitiativeFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setIsSubmitting(true);
    try {
      const savedInitiativePublicId = isEditing
        ? await updateInitiative({
            initiativePublicId,
            ...toInitiativePayload(values),
          }).unwrap()
        : await createInitiative(toInitiativePayload(values)).unwrap();

      await persistDocuments(savedInitiativePublicId);
      toast.success(intl.formatMessage({ id: "initiative.saveSuccess" }));
      if (!isEditing) {
        navigateAfterSaveRef.current = `/initiatives/${savedInitiativePublicId}`;
      }
      setHasUnsavedChanges(false);
    } catch {
      toast.error(intl.formatMessage({ id: "initiative.saveError" }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleValuesChange = (changed: Partial<IInitiativeFormValues>) => {
    setHasUnsavedChanges(true);
  
    if ("productLine0" in changed) {
      form.setFieldsValue({ productLine1: undefined, productLine2: undefined });
    }
  };

  return (
    <PageWrapper
      pageTitle={intl.formatMessage({
        id: isEditing ? "initiative.editPageTitle" : "initiative.createPageTitle",
      })}
      backButtonRoute={routes.initiatives.list}
    >
      <BlockingPopupModal
        isBlocking={hasUnsavedChanges}
        title={intl.formatMessage({ id: "initiative.unsavedChangesTitle" })}
        message={intl.formatMessage({ id: "initiative.unsavedChangesMessage" })}
        onOk={() => setHasUnsavedChanges(false)}
      />
      <Form
        form={form}
        layout="vertical"
        preserve
        scrollToFirstError
        initialValues={{ digital: false }}
        onValuesChange={handleValuesChange}
      >
        <StickyActionsHeader
          rightChildren={
            <>
              {canPromote && (
                <PromoteToRoadmap
                  pageName={PAGE_NAME}
                  loading={isPromoting}
                  disabled={isSubmitting}
                  promoteOnClick={handlePromote}
                />
              )}
              <SaveAndCancel
                pageName={PAGE_NAME}
                loading={isSubmitting}
                saveOnClick={handleSave}
                cancelOnClick={handleCancel}
              />
            </>
          }
        />
        <InitiativeFormFields
          projectTypeOptions={projectTypeOptions}
          productCategoryOptions={productCategoryOptions}
          fileList={fileList}
          initiativePublicId={initiativePublicId}
          placeholderId={placeholderId}
          productLine0Options={productLine0Options}
          productLine1Options={productLine1Options}
          productLine2Options={productLine2Options}
          opportunityTypeOptions={opportunityTypeOptions}
          engineeringComplexityOptions={engineeringComplexityOptions}
          commercialComplexityOptions={commercialComplexityOptions}
          fitToServiceOptions={fitToServiceOptions}
          requirePromotionFields={
            hasPromoted || initiative?.status === "RoadMapped"
          }
          beforeUpload={(file) => {
            setFileList((previousFileList) => [...previousFileList, file]);
            setHasUnsavedChanges(true);
          }}
          onRemove={handleRemoveDocument}
          onChange={handleDocumentsChange}
          onPreview={(file) => setPreviewDoc(file)}
        />
      </Form>
      {cdnFilePath && (
        <InitiativePreviewModal
          open={isModalOpen}
          cdnFilePath={cdnFilePath}
          previewFile={previewFile}
          onCancel={() => setIsModalOpen(false)}
          onDownload={downloadAndCloseModal}
        />
      )}
    </PageWrapper>
  );
};

export default Initiative;
